import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Cookie, X } from 'lucide-react';

const cookieConsentTranslations = {
  en: {
    title: 'Cookie Consent',
    message: 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies.',
    acceptAll: 'Accept All',
    acceptEssential: 'Essential Only',
    customize: 'Cookie Settings',
    learnMore: 'Learn more in our',
    cookiePolicy: 'Cookie Policy',
    essentialTitle: 'Essential Cookies',
    essentialDesc: 'Required for the website to function properly. Cannot be disabled.',
    analyticsTitle: 'Analytics Cookies',
    analyticsDesc: 'Help us understand how visitors interact with our website.',
    marketingTitle: 'Marketing Cookies',
    marketingDesc: 'Used to deliver relevant advertisements and track campaign effectiveness.',
    preferencesTitle: 'Preference Cookies',
    preferencesDesc: 'Remember your language and other preferences.',
    savePreferences: 'Save Preferences',
    close: 'Close'
  },
  es: {
    title: 'Consentimiento de Cookies',
    message: 'Utilizamos cookies para mejorar tu experiencia de navegación, analizar el tráfico del sitio y personalizar el contenido. Al hacer clic en "Aceptar Todo", aceptas nuestro uso de cookies.',
    acceptAll: 'Aceptar Todo',
    acceptEssential: 'Solo Esenciales',
    customize: 'Configuración de Cookies',
    learnMore: 'Más información en nuestra',
    cookiePolicy: 'Política de Cookies',
    essentialTitle: 'Cookies Esenciales',
    essentialDesc: 'Necesarias para que el sitio web funcione correctamente. No se pueden desactivar.',
    analyticsTitle: 'Cookies de Análisis',
    analyticsDesc: 'Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.',
    marketingTitle: 'Cookies de Marketing',
    marketingDesc: 'Se utilizan para mostrar anuncios relevantes y medir la efectividad de las campañas.',
    preferencesTitle: 'Cookies de Preferencias',
    preferencesDesc: 'Recuerdan tu idioma y otras preferencias.',
    savePreferences: 'Guardar Preferencias',
    close: 'Cerrar'
  },
  fr: {
    title: 'Consentement aux Cookies',
    message: 'Nous utilisons des cookies pour améliorer votre expérience de navigation, analyser le trafic du site et personnaliser le contenu. En cliquant sur "Tout Accepter", vous consentez à notre utilisation des cookies.',
    acceptAll: 'Tout Accepter',
    acceptEssential: 'Essentiels Uniquement',
    customize: 'Paramètres des Cookies',
    learnMore: 'En savoir plus dans notre',
    cookiePolicy: 'Politique de Cookies',
    essentialTitle: 'Cookies Essentiels',
    essentialDesc: 'Nécessaires au bon fonctionnement du site web. Ne peuvent pas être désactivés.',
    analyticsTitle: 'Cookies d\'Analyse',
    analyticsDesc: 'Nous aident à comprendre comment les visiteurs interagissent avec notre site web.',
    marketingTitle: 'Cookies Marketing',
    marketingDesc: 'Utilisés pour afficher des publicités pertinentes et mesurer l\'efficacité des campagnes.',
    preferencesTitle: 'Cookies de Préférences',
    preferencesDesc: 'Mémorisent votre langue et autres préférences.',
    savePreferences: 'Enregistrer les Préférences',
    close: 'Fermer'
  }
};

const CookieConsentBanner = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  const t = cookieConsentTranslations[language] || cookieConsentTranslations.en;

  // Get the language prefix for URLs
  const langPrefix = language === 'en' ? '' : `/${language}`;

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleAcceptEssential = () => {
    const consent = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop for settings modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Main Banner */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-500 ${
          showSettings ? 'translate-y-full' : 'translate-y-0'
        }`}
        data-testid="cookie-consent-banner"
      >
        <div className="bg-gray-900 border-t border-gray-700 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              {/* Icon and Message */}
              <div className="flex items-start gap-3 flex-1">
                <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">{t.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {t.message}{' '}
                    <Link 
                      to={`${langPrefix}/cookie-policy`}
                      className="text-green-400 hover:text-green-300 underline"
                    >
                      {t.cookiePolicy}
                    </Link>
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-800 transition-colors"
                  data-testid="cookie-settings-btn"
                >
                  {t.customize}
                </button>
                <button
                  onClick={handleAcceptEssential}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  data-testid="cookie-essential-btn"
                >
                  {t.acceptEssential}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 text-sm font-bold text-white bg-green-600 rounded-lg hover:bg-green-500 transition-colors"
                  data-testid="cookie-accept-all-btn"
                >
                  {t.acceptAll}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div 
          className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:bottom-8 sm:max-w-lg w-full z-[9999] bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid="cookie-settings-modal"
        >
          <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">{t.customize}</h3>
            <button 
              onClick={() => setShowSettings(false)}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* Essential Cookies */}
            <div className="py-4 border-b">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{t.essentialTitle}</h4>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Always On</span>
              </div>
              <p className="text-sm text-gray-600">{t.essentialDesc}</p>
            </div>

            {/* Analytics Cookies */}
            <div className="py-4 border-b">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{t.analyticsTitle}</h4>
                <button
                  onClick={() => togglePreference('analytics')}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    preferences.analytics ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                  data-testid="cookie-toggle-analytics"
                >
                  <span 
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      preferences.analytics ? 'right-1' : 'left-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-600">{t.analyticsDesc}</p>
            </div>

            {/* Marketing Cookies */}
            <div className="py-4 border-b">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{t.marketingTitle}</h4>
                <button
                  onClick={() => togglePreference('marketing')}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    preferences.marketing ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                  data-testid="cookie-toggle-marketing"
                >
                  <span 
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      preferences.marketing ? 'right-1' : 'left-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-600">{t.marketingDesc}</p>
            </div>

            {/* Preference Cookies */}
            <div className="py-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{t.preferencesTitle}</h4>
                <button
                  onClick={() => togglePreference('preferences')}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    preferences.preferences ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                  data-testid="cookie-toggle-preferences"
                >
                  <span 
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      preferences.preferences ? 'right-1' : 'left-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-600">{t.preferencesDesc}</p>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
            <button
              onClick={() => setShowSettings(false)}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t.close}
            </button>
            <button
              onClick={handleSavePreferences}
              className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-green-600 rounded-lg hover:bg-green-500 transition-colors"
              data-testid="cookie-save-preferences-btn"
            >
              {t.savePreferences}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
